import { Link } from "react-router-dom";
import styled from "styled-components";
import { Paragraph, ParagraphWithLightBorder, Title } from "./About";
import { Button, Input, InputContainer as BaseInputContainer, TextArea } from "./CallBack";
import { WrapperContainer } from "./SliderItem";
import { smDown } from "../utils/responsive";
import { useLayoutEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import emailjs from "@emailjs/browser";

const SERVICE_ID = "service_e5g3pig";
const TEMPLATE_ID = "template_romk8qh";
const PUBLIC_KEY = "GTUWxDDlO2Nswdvjo";

const Container = styled(WrapperContainer)`
  padding: 48px 12px;
`;

const Wrapper = styled.div`
  margin: 0 -12px;
  display: flex;
  flex-wrap: wrap;
`;

const Col = styled.div`
  padding: 0 12px;
  width: 50%;
  ${smDown({
    width: "100%",
  })}
`;

const Form = styled.form`
  display: flex;
  flex-wrap: wrap;
`;

const InputContainer = styled(BaseInputContainer)`
  width: 100%;
  margin-bottom: 16px;
`;

const FormButton = styled(Button)`
  margin: 16px 8px;
`;

const Iframe = styled.iframe`
  width: 100%;
  height: 100%;
  min-height: 450px;
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadius};
`;

const Error = styled.p`
  color: red;
  font-size: 14px;
  margin-top: -10px;
  margin-bottom: 10px;
`;

const Contact = () => {
  const containerEl = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [status, setStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useLayoutEffect(() => {
    const contactTween = gsap.from(containerEl.current, {
      opacity: 0,
      scrollTrigger: {
        trigger: containerEl.current,
      },
    });

    return () => {
      if (contactTween) contactTween.kill();
    };
  }, []);

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Invalid email";
    if (!formData.phone) newErrors.phone = "Phone number is required";
    if (!formData.subject) newErrors.subject = "Subject is required";
    if (!formData.message) newErrors.message = "Message is required";
    return newErrors;
  };

  const handleChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    setErrors({ ...errors, [field]: "" });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setStatus("Sending...");
    setIsSubmitting(true);
    emailjs
      .send(SERVICE_ID, TEMPLATE_ID, formData, PUBLIC_KEY)
      .then(() => {
        alert("Message sent successfully!");
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      })
      .catch(() => {
        setStatus("Something went wrong. Please try again.");
      }).finally(()=>{
        setIsSubmitting(false);
      })
  };

  return (
    <Container ref={containerEl}>
      <Wrapper>
        <Col>
          <ParagraphWithLightBorder>Contact</ParagraphWithLightBorder>
          <Title>If You Have Any Query, Please Contact Us</Title>

          <Form onSubmit={handleSubmit}>
            <InputContainer>
              <Input
                placeholder="Your Name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
              />
              {errors.name && <Error>{errors.name}</Error>}
            </InputContainer>

            <InputContainer>
              <Input
                placeholder="Your Email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
              />
              {errors.email && <Error>{errors.email}</Error>}
            </InputContainer>

            <InputContainer>
              <Input
                placeholder="Phone Number"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
              />
              {errors.phone && <Error>{errors.phone}</Error>}
            </InputContainer>

            <InputContainer>
              <Input
                placeholder="Subject"
                value={formData.subject}
                onChange={(e) => handleChange("subject", e.target.value)}
              />
              {errors.subject && <Error>{errors.subject}</Error>}
            </InputContainer>

            <InputContainer>
              <TextArea
                placeholder="Message"
                value={formData.message}
                onChange={(e) => handleChange("message", e.target.value)}
              />
              {errors.message && <Error>{errors.message}</Error>}
            </InputContainer>
            <FormButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : "Send Message"}
            </FormButton>
          
          </Form>
        </Col>

        <Col>
          <Iframe
            src="https://www.google.com/maps/embed?pb=!1m24!1m12!1m3!1d507738.91301232454!2d-1.2245601382689313!3d6.169538375074789!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!4m9!3e6!4m3!3m2!1d5.6352541!2d-0.1775958!4m3!3m2!1d6.689354799999999!2d-1.610665!5e0!3m2!1sen!2sgh!4v1745408031965!5m2!1sen!2sgh"
            loading="lazy"
            title="Locate Us"
          />
        </Col>
      </Wrapper>
    </Container>
  );
};

export default Contact;
